<template>
  <div class="">
    <div>
      <h3 class="">{{ $t('report.diagnosis') }}</h3>
      <p>{{ $t('report.anyAlteration') }}</p>
    </div>

    <div class="new-alteration-box" v-if="isDataReady">
      <div class="eye-alteration-box" v-for="(item, index) in localFormData.eyeAlterationAdded" :id="item.id" :key="item.id">
        <div class="form-floating v-select-floating">
          <v-select
            class="v-select form"
            id="selectEye"
            v-model="item.selectedEye"
            label="label"
            :reduce="x => x.value"
            :options="eyeOptions"
            @option:selected="switchEye(item, $event)"
            :placeholder="$t('report.whichEye')"
            data-test="vSelectEye">
          </v-select>
          <label for="selectTemplate">{{ $t('report.whichEye') }}</label>
        </div>

        <div class="form-floating v-select-floating">
          <v-select
            class="v-select form"
            id="selectAlteration"
            v-model="item.selectedAlteration"
            label="label"
            :reduce="x => x.value"
            :options="getAvailableAlterations(item.id, item.selectedEye)"
            :class="{'disabled': item.selectedEye == null}"
            :disabled="item.selectedEye == null"
            @option:selected="switchAlteration(item, $event)"
            :placeholder="$t('report.alteration')"
            data-test="vSelectTemplate">
          </v-select>
          <label for="selectTemplate">{{ $t('report.alteration') }}</label>
        </div>

        <button class="btn btn-outline-danger" type="button" name="button" @click="removeEyeAlterationAdded(index)">
          <IconMinus/>
        </button>
      </div>

      <button class="btn btn-outline-success" type="button" name="button" @click="addEyeAlteration">
        <IconPlus/>
        {{ $t('report.addAlteration') }}
      </button>
    </div>

    <div class="form-floating" v-if="!isQuickReport">
      <select id="diagnosis_select" class="js-example-basic-multiple js-states form-control" name="diagnosis_select" multiple="multiple"
        style="width:100%;" :placeholder="$t('exam.diagnosisCID10')" data-test="selectDiagnosis"></select>
      <label for="diagnosis_select">{{ $t('exam.diagnosisCID10') }}</label>

      <div class="selected-diagnosis-box" id="selectedDiagnosis" v-if="localFormData.selectedDiagnosis.length">
        <span v-for="item in localFormData.selectedDiagnosis" :key="item.id" class="tag2 alert" role="tag"
              :data-value="item.id">
          <span>{{ item.text }}</span>
          <a href="#" @click="removeDiagnosisEntry(item.id)">
            <IconCross/>
          </a>
        </span>
      </div>
    </div>

    <div class="">
      <p>{{ $t('report.textualObservations') }}</p>
    </div>

    <div class="form-floating v-select-floating" v-if="!isQuickReport">
      <v-select
        class="v-select form"
        id="selectTemplate"
        v-model="localFormData.selectedTemplate"
        label="name"
        :reduce="x => x.id"
        :options="templateOptions"
        @option:selected="switchTemplate"
        :placeholder="'Template'"
        data-test="vSelectTemplate">
      </v-select>
      <label for="selectTemplate">Template</label>
    </div>

    <ReportForm
      v-if="isDataReady && !isQuickReport"
      ref="reportForm"
      :content="localFormData.customTemplate"
      @custom-template="setCustomTemplate"/>

    <div class="form-floating v-select-floating">
      <v-select
        class="v-select form"
        :class="{'disabled': localFormData.selectedTemplate != null}"
        id="selectColumns"
        v-model="localFormData.columns"
        :reduce="x => x.value"
        :options="columnOptions"
        :placeholder="$t('report.columnNumber')"
        :disabled="localFormData.selectedTemplate != null"
        data-test="vSelectColumns">
      </v-select>
      <label for="selectColumns">{{ $t('report.columnNumber') }}</label>
    </div>

    <div class="form-floating">
      <div class="form-check form-switch">
        <label class="form-check-label" for="fullWidthPanoramic">{{ $t('exam.panoProcessOnReport') }}</label>
        <input class="form-check-input" type="checkbox" role="switch" id="fullWidthPanoramic" v-model="localFormData.fullWidthPanoramic" data-test="checkboxFullWidthPanoramic">
      </div>
    </div>

    <div class="form-floating">
      <textarea class="form-control" id="observation" name="name" rows="3" v-model="localFormData.observation"
                :placeholder="$t('exam.observations')" data-test="textareaObservations"></textarea>
      <label for="observation">{{ $t('exam.observations') }}</label>
    </div>

    <teleport to="body">
      <BaseGenericOffcanvas :offcanvas_id="'offcanvasReportUnifiedGeneric'" ref="offcanvasReportUnifiedGeneric"/>
    </teleport>
  </div>
</template>

<style lang="scss" scoped>
h3,
p {
  font-weight:                     400;
  line-height:                     normal;
  text-overflow:                   ellipsis;
  white-space:                     nowrap;
  overflow:                        hidden;
}

h3 {
  color:                           var(--Shades-60);
  font-size:                       var(--font-size-14);
  letter-spacing:                  2.8px;
  text-transform:                  uppercase;
  margin-bottom:                   10px;
}

p {
  color:                           var(--Shades-90);
  font-size:                       var(--font-size-12);
  margin:                          0;
}

.new-alteration-box {
  .btn-outline-success {
    color:                         var(--Brand-Primary);
    font-size:                     var(--font-size-12);
    font-weight:                   700;
    line-height:                   normal;
    letter-spacing:                3.6px;
    text-align:                    center;
    text-transform:                uppercase;
    display:                       flex;
    flex-direction:                row;
    align-items:                   center;
    align-self:                    stretch;
    gap:                           10px;
    width:                         100%;
    padding:                       20px;
    border:                        2px solid var(--Support-Success);
    background:                    var(--Shades-10);
  }
}

.eye-alteration-box {
  display:                         flex;
  flex-direction:                  row;
  justify-content:                 space-between;
  gap:                             10px;
  margin-bottom:                   20px;

  > *:not(:last-child) {
    flex:                          1;

    &:nth-child(2) {
      flex:                        2;
    }
  }

  :deep(.vs__selected),
  :deep(.vs__dropdown-option) {
    text-transform:                capitalize;
  }

  .btn-outline-danger {
    width:                         55px;
    height:                        55px;
    background:                    var(--Shades-10);
    border:                        1px solid var(--Shades-30);
  }
}

:deep(.select2-container .select2-selection--multiple .select2-selection__rendered) {
  display:                         none !important;
}

@media (max-width: 768px) {
  .eye-alteration-box {
    gap:                           5px;

    > *:not(:last-child) {
      min-width:                   66px;

      &:nth-child(2) {
        max-width:                 60%;
      }
    }
  }
}
</style>

<script>
import ReportForm from './ReportForm.vue'

const BASE_URL = process.env.VUE_APP_API_URL

export default {
  name: 'ReportUnified',
  props: {
    templateOptions: {type: Object, required: true},
    columnOptions: {type: Object, required: true},
    currentReport: {type: Object, required: false},
    selectedImages: {type: Array, required: false},
    examImages: {type: Array, required: false},
    examId: {type: Number, required: false},
    unifiedExamType: {type: String, required: false},
    reportStatus: {type: String, required: false}
  },
  components: {
    ReportForm
  },
  data: () => ({
    loading: false,
    alert: {},
    quickReportEntries: {
      right: [],
      left: [],
    },
    loadedDiagnosisEntries: {
      right: [],
      left: []
    },
    customTemplate: null,
    isDataReady: false,
    eyeOptions: [],
    selectedEye: null,
    alterationOptions: [],
    selectedAlteration: null,
    eyeAlterationNextID: 1,
    localFormData: {
      selectedTemplate: null,
      selectedDiagnosis: [],
      customTemplate: null,
      columns: 2,
      fullWidthPanoramic: false,
      observation: '',
      eyeAlterationAdded: [],
    },
    isQuickReport: false
  }),
  watch: {
    currentReport: {
      immediate: true,
      deep: true,
      handler(newValue) {
        if (newValue && newValue.id)
          this.initializeUnifiedReportData(newValue)
        else
          this.resetFormData()
      }
    }
  },
  created() {
    this.eyeOptions = [
      {value: 'OD', label: this.$i18n.t('od')},
      {value: 'OS', label: this.$i18n.t('os')}
    ]

    this.alterationOptions = [
      {value: '001_1', label: this.$i18n.t('quickReportEntry.001_1')},
      {value: '001_2', label: this.$i18n.t('quickReportEntry.001_2')},
      {value: '001_3', label: this.$i18n.t('quickReportEntry.001_3')},
      {value: '001_4', label: this.$i18n.t('quickReportEntry.001_4')},
      {value: '002_1', label: this.$i18n.t('quickReportEntry.002_1')},
      {value: '003_1', label: this.$i18n.t('quickReportEntry.003_1')},
      {value: '003_2', label: this.$i18n.t('quickReportEntry.003_2')},
      {value: '003_3', label: this.$i18n.t('quickReportEntry.003_3')},
      {value: '003_4', label: this.$i18n.t('quickReportEntry.003_4')},
      {value: '003_5', label: this.$i18n.t('quickReportEntry.003_5')},
      {value: '003_6', label: this.$i18n.t('quickReportEntry.003_6')},
      {value: '003_7', label: this.$i18n.t('quickReportEntry.003_7')},
      {value: '004_1', label: this.$i18n.t('quickReportEntry.004_1')},
      {value: '005_1', label: this.$i18n.t('quickReportEntry.005_1')},
      {value: '005_2', label: this.$i18n.t('quickReportEntry.005_2')},
      {value: '005_3', label: this.$i18n.t('quickReportEntry.005_3')},
      {value: '006_1', label: this.$i18n.t('quickReportEntry.006_1')}
    ]
  },
  mounted() {
    this.localFormData.eyeAlterationAdded = [];
    this.eyeAlterationNextID = 1;

    this.initializeView();
    $('#selectedDiagnosis').innerHTML = '' // avoid duplicated CID's

    this.isDataReady = true
  },
  methods: {
    resetFormData() {
      this.localFormData = {
        selectedTemplate: null,
        selectedDiagnosis: [],
        customTemplate: null,
        columns: 2,
        fullWidthPanoramic: false,
        observation: '',
        eyeAlterationAdded: [],
      };

      this.eyeAlterationNextID = 1;
      this.isDataReady = false;

      if (this.$refs.reportForm)
        this.$refs.reportForm.reloadEditorContent('')
    },
    initializeUnifiedReportData(report) {
      this.localFormData = {
        selectedTemplate: report.selectedTemplate,
        selectedDiagnosis: [],
        customTemplate: report.customTemplate,
        columns: report.columns || 2,
        fullWidthPanoramic: report.fullWidthPanoramic || false,
        observation: report.observation || '',
        eyeAlterationAdded: [],
      };

      this.isQuickReport = report.isQuickReport

      this.eyeAlterationNextID = 1;

      $('#diagnosis_select').val(null).trigger('change');

      this.$nextTick(() => {
        if (report.patientDiagnosis)
          this.loadEyeAlterations(report.patientDiagnosis)

        if (report.diagnosisReferences)
          this.loadCompleteReportDiagnosis(report.diagnosisReferences)

        if (this.$refs.reportForm)
          this.$refs.reportForm.reloadEditorContent(this.localFormData.customTemplate)

        this.isDataReady = true;
      });
    },
    switchTemplate() {
      if (this.localFormData.selectedTemplate == null) {
        this.localFormData.customTemplate = ""
        this.$refs.reportForm.reloadEditorContent(this.localFormData.customTemplate)
        return
      }

      for (let t of this.templateOptions) {
        if (t.id == this.localFormData.selectedTemplate) {
          this.localFormData.customTemplate = t.content
          this.localFormData.columns = t.columnsDefault
          break
        }
      }

      this.$refs.reportForm.reloadEditorContent(this.localFormData.customTemplate)
    },
    setCustomTemplate(value) {
      this.localFormData.customTemplate = value;
    },
    addDiagnosisEntry(id, text) {
      if (!this.localFormData.selectedDiagnosis.find(d => d.id === id))
        this.localFormData.selectedDiagnosis.push({ id: id, text: text });
    },
    removeDiagnosisEntry(id) {
      this.localFormData.selectedDiagnosis = this.localFormData.selectedDiagnosis.filter(d => d.id !== id);
    },
    loadCompleteReportDiagnosis(diagnosisReferences) {
      this.localFormData.selectedDiagnosis = [];
      if (diagnosisReferences) {
        const selectedUuids = [];
        diagnosisReferences.forEach(o => {
          if (o.type && o.type.uuid && o.type.key && o.type.description) {
            this.addDiagnosisEntry(o.type.uuid, `${o.type.key} - ${o.type.description}`);
            selectedUuids.push(o.type.uuid);
          }
        });

        this.$nextTick(() => {
          $('#diagnosis_select').val(selectedUuids).trigger('change');
        });
      }
    },
    initializeView() {
      let vm = this;
      $('#diagnosis_select').select2({
        "language": {
          errorLoading: () => {
            return this.$i18n.t('request_error');
          },
          inputTooShort: () => {
            return this.$i18n.t('cid.inputTooShort');
          },
          noResults: () => {
            return this.$i18n.t('request_empty');
          },
          searching: () => {
            return this.$i18n.t('cid.searching');
          },
        },
        minimumInputLength: 1,
        ajax: {
          url: BASE_URL + '/api/v2/eyercloud/report/cid10-search',
          xhrFields: {
            withCredentials: true
          },
          data: function (params) {
            if (!params.term) {
              return;
            }

            let query = {
              search: params.term
            }
            return query;
          },
        }
      });

      $('#diagnosis_select').on('select2:select', function (e) {
        let data = e.params.data;

        if (!vm.localFormData.selectedDiagnosis.find(d => d.id === data.id))
          vm.addDiagnosisEntry(data.id, data.text);

        $(this).val('').trigger("change");
      });

      $(document).on('click', '.selected-diagnosis-box .tag2 a', function(e) {
        e.preventDefault();
        const id = $(this).parent().data('value');
        vm.removeDiagnosisEntry(id);
      });
    },
    switchEye(item, selectedOption) {
      const newEye = selectedOption.value;
      const currentAlteration = item.selectedAlteration;

      if (currentAlteration) {
        const availableOptionsForNewEye = this.getAvailableAlterations(item.id, newEye);
        const alterationStillAvailableForNewEye = availableOptionsForNewEye.some(option => option.value === currentAlteration);

        if (!alterationStillAvailableForNewEye)
          item.selectedAlteration = null;
      }
    },
    switchAlteration(item, selectedOption) {
      item.selectedAlteration = String(selectedOption.value);
    },
    addEyeAlteration() {
      this.isQuickReport = true

      if (this.localFormData.columns != 2)
        this.localFormData.columns = 2

      if (this.localFormData.selectedTemplate != null) {
        this.localFormData.selectedTemplate = null
        this.setCustomTemplate('')
        this.$refs.reportForm.reloadEditorContent(this.localFormData.customTemplate)
      }

      this.localFormData.eyeAlterationAdded.push({
        id: this.eyeAlterationNextID++,
        selectedEye: null,
        selectedAlteration: null
      });
    },
    removeEyeAlterationAdded(index) {
      this.localFormData.eyeAlterationAdded.splice(index, 1);

      if (this.localFormData.eyeAlterationAdded.length == 0) {
        this.isQuickReport = false

        this.$nextTick(() => {
          this.initializeView()
          $('#selectedDiagnosis').innerHTML = ''
        })
      }
    },
    getAvailableAlterations(currentItemId, currentSelectedEye) {
      if (!currentSelectedEye)
        return this.alterationOptions;

      const selectedAlterationIdsForCurrentEye = this.localFormData.eyeAlterationAdded
        .filter(item => {
          const isSameItem = item.id === currentItemId;
          const isSameEye = item.selectedEye === currentSelectedEye;
          const hasSelectedAlteration = !!item.selectedAlteration;
          return !isSameItem && isSameEye && hasSelectedAlteration;
        }).map(item => String(item.selectedAlteration));

      const availableOptions = this.alterationOptions.filter(option =>
        !selectedAlterationIdsForCurrentEye.includes(String(option.value))
      );

      return availableOptions;
    },
    loadEyeAlterations(patientDiagnosis) {
      this.eyeAlterationNextID = 1;
      const newEyeAlterations = [];
      const uniqueAlterationsLoaded = new Set();

      if (patientDiagnosis) {
        patientDiagnosis.forEach(diagnosisEntry => {
          let normalizedLaterality = null;

          if (typeof diagnosisEntry.laterality === 'string') {
            const lowerCaseLaterality = diagnosisEntry.laterality.toLowerCase();
            if (lowerCaseLaterality === 'right') {
              normalizedLaterality = 'OD';
            } else if (lowerCaseLaterality === 'left') {
              normalizedLaterality = 'OS';
            }
          }

          const normalizedKey = String(diagnosisEntry.key);

          if (normalizedKey && normalizedKey.startsWith('000_'))
            return

          if (normalizedLaterality && normalizedKey) {
            const uniqueKey = `${normalizedLaterality}_${normalizedKey}`;
            if (!uniqueAlterationsLoaded.has(uniqueKey)) {
              newEyeAlterations.push({
                id: this.eyeAlterationNextID++,
                selectedEye: normalizedLaterality,
                selectedAlteration: normalizedKey
              });
              uniqueAlterationsLoaded.add(uniqueKey);
            }
          }
        });
      }

      this.localFormData.eyeAlterationAdded.splice(0, this.localFormData.eyeAlterationAdded.length, ...newEyeAlterations);
    },
    mapEyeAlterationsToBackend() {
      const quickReportEntries = { right: [], left: [] }
      const hasRightAlterations = this.localFormData.eyeAlterationAdded.some(item => item.selectedEye === 'OD' && item.selectedAlteration)
      const hasLeftAlterations = this.localFormData.eyeAlterationAdded.some(item => item.selectedEye === 'OS' && item.selectedAlteration)

      hasRightAlterations ? quickReportEntries.right.push('000_2') : quickReportEntries.right.push('000_1')
      hasLeftAlterations ? quickReportEntries.left.push('000_2') : quickReportEntries.left.push('000_1')

      this.localFormData.eyeAlterationAdded.forEach(item => {
        if (item.selectedEye === 'OD') {
          quickReportEntries.right.push(item.selectedAlteration);
        } else if (item.selectedEye === 'OS') {
          quickReportEntries.left.push(item.selectedAlteration);
        }
      });

      return quickReportEntries;
    },
    async doSave() {
      const newReport = {
        ...this.localFormData,
        isUnifiedReport: true,
        isQuickReport: this.isQuickReport,
        isHedisReport: false,
        exam: this.examId,
        selectedImages: this.selectedImages.filter(item => item !== null),
        selectedDiagnosis: this.localFormData.selectedDiagnosis.map(d => d.id),
        quickReportEntries: this.mapEyeAlterationsToBackend(),
        reportStatus: this.reportStatus,
      };

      if (this.currentReport.id)
        newReport.id = this.currentReport.id

      if (this.examImages && this.examImages.length > 0 && newReport.selectedImages.length === 0) {
        const ok = await this.$refs.offcanvasReportUnifiedGeneric.show({
          title: this.$i18n.t('confirm'),
          text: this.$i18n.t('report.imageReportConfirmation'),
          okButton: this.$i18n.t('yes'),
          okButtonClass: 'btn-danger',
          cancelButton: this.$i18n.t('no'),
          cancelButtonClass: 'btn-outline-secondary'
        })
        if (!ok) {
          return;
        }
      }

      this.loading = true
      NProgress.start()
      try {
        // let response = await this.$reportService.saveUnifiedReport(newReport)
        // if we are going to use CID with quickReport, we need to use the new endpoint
        let response = await this.$reportService.saveReport(newReport)

        if (this.unifiedExamType == 'noQuality')
          await this.setAlteration()

        if (this.currentReport.id) {
          this.$message.popup(this, 'general.updatedSuccess', 'operationSuccess', 'success')
        } else {
          this.$message.popup(this, 'general.createdSuccess', 'operationSuccess', 'success')
        }

        this.$nextTick(() => {
          this.$emit('reportGenerated', response.result.id)
        })
      } catch (err) {
        this.$message.popup(this, 'general.createdError', 'operationFail', 'error')
        // this.alert = this.$message.alert(this, 'general.createdError', 'danger');
      } finally {
        NProgress.done(true)
        this.loading = false
      }
    },
    async setAlteration() {
      let params = {
        examId: this.examId,
        type: 'altered'
      }

      this.loading = true
      NProgress.start()
      try {
        let response = await this.$examService.updateUnifiedExam(params)
      } catch (e) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
      } finally {
        NProgress.done(true)
        this.loading = false
      }
    },
  }
}
</script>
